import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useLanguage } from '../../contexts/LanguageContext';
import { BranchIssueRequestDto } from '../../types/branchIssueRequests.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

const PURPLE = '#7b1fa2';

interface BranchIssueRequestPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  request: BranchIssueRequestDto | null;
}

const BranchIssueRequestPreviewDialog: React.FC<BranchIssueRequestPreviewDialogProps> = ({
  open,
  onClose,
  request,
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }
  }, [open]);

  if (!request) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Pending': return isRTL ? 'قيد الانتظار' : 'Pending';
      case 'Approved': return isRTL ? 'معتمد' : 'Approved';
      case 'Rejected': return isRTL ? 'مرفوض' : 'Rejected';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatNumber = (num?: number) => (num ?? 0).toFixed(3);

  const handlePrint = () => {

    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid ${PURPLE};padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:${PURPLE};margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'طلب إصدار للفرع' : 'Branch Issue Request';

    const detailRows = request.details && request.details.length > 0
      ? request.details.map((d, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td><strong>${d.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? d.itemNameAr : d.itemName}</span></td>
          <td>${isRTL ? d.unitNameAr : d.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.requestedQuantity)}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.approvedQuantity)}</td>
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="6" style="text-align:center;color:#999;">${isRTL ? 'لا توجد تفاصيل' : 'No details'}</td></tr>`;

    const totalItems = request.details?.length || 0;

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle} - ${request.requestNumber}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 13px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px 10px; font-size: 12px; }
          th { background: ${PURPLE}; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .info-item label { font-size: 11px; color: #999; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .status { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
          .status-Pending { background: #ed6c02; }
          .status-Approved { background: #2e7d32; }
          .status-Rejected { background: #d32f2f; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #000; padding-top: 5px; margin-top: 60px; }
          @page { size: A4; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:${PURPLE};margin:10px 0 20px;">${docTitle}</h1>

        <div style="border:2px solid ${PURPLE};border-radius:8px;padding:15px;margin-bottom:20px;">
          <div class="info-grid">
            <div class="info-item">
              <label>${isRTL ? 'رقم الطلب' : 'Request Number'}</label>
              <span style="color:${PURPLE};">${request.requestNumber}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'التاريخ' : 'Date'}</label>
              <span>${formatDate(request.requestDate)}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'الحالة' : 'Status'}</label>
              <span class="status status-${request.status}">${getStatusLabel(request.status)}</span>
            </div>
            ${request.issueNumber ? `
            <div class="info-item">
              <label>${isRTL ? 'رقم سند الإصدار' : 'Issue Number'}</label>
              <span style="color:#2e7d32;">${request.issueNumber}</span>
            </div>` : ''}
          </div>

          <hr style="border:none;border-top:1px solid #ddd;margin:10px 0;" />

          <div class="info-item" style="margin-bottom:5px;">
            <label>${isRTL ? 'الفرع' : 'Branch'}</label>
            <span style="color:${PURPLE};font-size:15px;">${isRTL ? (request.branchNameAr || request.branchName) : request.branchName}</span>
            ${request.branchCode ? `<span style="font-size:11px;color:#999;margin-${isRTL ? 'right' : 'left'}:8px;">${request.branchCode}</span>` : ''}
          </div>

          ${request.notes ? `<div style="margin-top:10px;"><label style="font-size:11px;color:#999;">${isRTL ? 'ملاحظات:' : 'Notes:'}</label><p>${request.notes}</p></div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${isRTL ? 'الصنف' : 'Item'}</th>
              <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
              <th>${isRTL ? 'الكمية المطلوبة' : 'Requested Qty'}</th>
              <th>${isRTL ? 'الكمية المعتمدة' : 'Approved Qty'}</th>
              <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            <tr style="background:#f3e5f5;font-weight:bold;">
              <td colspan="3" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي الأصناف:' : 'Total Items:'}</td>
              <td style="text-align:center;" colspan="3">${totalItems}</td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير الفرع' : 'Branch Manager'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير العمليات' : 'Operations Manager'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'أمين المستودع' : 'Warehouse Keeper'}</div></div>
        </div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)'
            : `linear-gradient(135deg, ${PURPLE} 0%, #ab47bc 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon />
          <Box component="span">{isRTL ? 'معاينة طلب الإصدار' : 'Issue Request Preview'}</Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم الطلب' : 'Request Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: PURPLE }}>{request.requestNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{formatDate(request.requestDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box><Chip label={getStatusLabel(request.status)} color={getStatusColor(request.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {request.issueNumber && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم سند الإصدار' : 'Issue Number'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{request.issueNumber}</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Branch */}
          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(123,31,162,0.1)' : '#f3e5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {isRTL ? 'الفرع' : 'Branch'}
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ color: PURPLE }}>
              {isRTL ? (request.branchNameAr || request.branchName) : request.branchName}
            </Typography>
            {request.branchCode && (
              <Typography variant="caption" color="text.secondary">{request.branchCode}</Typography>
            )}
          </Box>

          {request.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">{request.notes}</Typography>
            </Box>
          )}

          {request.rejectionReason && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="error" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'سبب الرفض:' : 'Rejection Reason:'}
              </Typography>
              <Typography variant="body2" color="error">{request.rejectionReason}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(123,31,162,0.3)' : PURPLE, color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? 'تفاصيل الأصناف' : 'Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية المطلوبة' : 'Requested Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية المعتمدة' : 'Approved Qty'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {request.details && request.details.length > 0 ? (
                  request.details.map((d, idx) => (
                    <TableRow key={d.branchIssueRequestDetailId || idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{d.itemCode}</strong><br />
                        <Typography variant="caption" color="text.secondary">{isRTL ? d.itemNameAr : d.itemName}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatNumber(d.requestedQuantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: d.approvedQuantity != null ? 'success.main' : 'text.secondary' }}>
                        {d.approvedQuantity != null ? formatNumber(d.approvedQuantity) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{d.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">{isRTL ? 'لا توجد تفاصيل' : 'No details'}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {request.details && request.details.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align={isRTL ? 'left' : 'right'} sx={{ fontWeight: 'bold', bgcolor: 'rgba(123,31,162,0.1)', fontSize: '0.85rem' }}>
                      {isRTL ? 'إجمالي الأصناف:' : 'Total Items:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(123,31,162,0.1)' }}>
                      <Chip label={request.details.length} sx={{ bgcolor: PURPLE, color: 'white', fontWeight: 'bold' }} size="small" />
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(123,31,162,0.1)' }} />
                    <TableCell sx={{ bgcolor: 'rgba(123,31,162,0.1)' }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? 'linear-gradient(45deg, #4a148c 30%, #6a1b9a 90%)' : `linear-gradient(45deg, ${PURPLE} 30%, #ab47bc 90%)` }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchIssueRequestPreviewDialog;
